import { useState } from "react";
import PageSizeSelector from "./PageSizeSelector";
import { TrashIcon } from "@heroicons/react/24/outline";
import DeleteModal from "./DeleteModal";
import TableActions from "./TableActions";
import {
    CheckboxCell,
    AppCell,
    EducatorCell,
    AdoptionCell,
    StatusCell,

} from "./TableCells";
import toast from "react-hot-toast";
import { applicationService } from "../services/applicationService";
import Loader from "./Loader";


const ApplicationTable = ({ loader, columns, data, showActions, page, setPage, pageLimit, setPageLimit, pagination, userRole, getAllApplicationList, getAllApplicationForFilter, permission }) => {
    const [selectedRows, setSelectedRows] = useState('');
    const [selectAll, setSelectAll] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false)

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    // Handle select all
    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        setSelectAll(checked);
        if (checked) {
            const allIds = data.map((item) => item?.application_id).join(",");
            setSelectedRows(allIds);
        } else {
            setSelectedRows("");
        }
    };

    // Handle row select
    const handleRowSelect = (id) => {
        if (selectedRows.includes(id)) {
            const currentIds = selectedRows ? selectedRows.split(",") : [];
            const newIdsArray = currentIds.filter((i) => i !== id.toString());
            const newIds = newIdsArray.join(",");
            setSelectedRows(newIds);
            setSelectAll(false);
        } else {
            const currentIds = selectedRows ? selectedRows.split(",") : [];
            const newIdsArray = [...currentIds, id.toString()];
            const newIds = newIdsArray.join(",");
            setSelectedRows(newIds);

            if (newIdsArray.length === data.length) {
                setSelectAll(true);
            }
        }
    };


    // Delete handler
    const handleDelete = async () => {
        try {
            setDeleteLoader(true)
            let data = JSON.stringify({
                'id': selectedRows
            })
            const response = await applicationService.deleteApplication(data)
            if (response?.success == true) {
                setDeleteLoader(false)
                toast.success(response?.message || 'Application deleted successfully')
                await getAllApplicationList()
                await getAllApplicationForFilter()
                setSelectedRows("")
                setIsDeleteOpen(false);
                setSelectAll(false)
            } else {
                setDeleteLoader(false)
                setSelectedRows("")
                setIsDeleteOpen(false);
                setSelectAll(false)
                // toast.error(response?.message)
            }
        } catch (e) {
            setDeleteLoader(false)
            setSelectedRows("")
            setIsDeleteOpen(false);
            setSelectAll(false)
            toast.error(e.response.data.message || 'Something went wrong')
        }
    };

    return (
        <div className="p-4">
            <div className="bg-white shadow rounded-2xl p-5 overflow-x-auto">
                {/* Top Row */}
                <div className="flex justify-between items-center mb-4">
                    <PageSizeSelector pageLimit={pageLimit} setPageLimit={setPageLimit} />
                    {permission?.deleteMultipal === true && selectedRows.length > 0 && (
                        <button
                            className="flex items-center gap-2 text-red-600 hover:text-red-800"
                        >
                            <TrashIcon
                                className="w-5 h-5 cursor-pointer text-red-600 hover:font-800"
                                onClick={() => setIsDeleteOpen(true)}
                            />
                        </button>
                    )}
                </div>

                {/* Table */}
                <div className="overflow-x-auto border border-gray-100 rounded-[20px] h-[530px]">
                    <table className="w-full min-w-[800px] text-left border-collapse">
                        <thead className="text-gray-700 text-sm sticky top-0 bg-white">
                            <tr>
                                <th className="py-4 px-3">
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                        className="h-5 w-5"
                                    />
                                </th>
                                {columns.map((col, index) => {
                                    if (col.key === "action" && permission?.view === false && permission?.updateApplicationProgress === false && permission?.edit === false && permission?.deleteSingle === false) {
                                        return null; // 👈 skip rendering this column
                                    }
                                    return (
                                        <th key={index} className="py-3 px-3 font-semibold">
                                            {col.label}
                                        </th>
                                    )
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {loader ?
                                <tr>
                                    <td colSpan={9}>
                                        <Loader />
                                    </td>
                                </tr>
                                :
                                (data && data.length > 0) ? data.map((item, idx) => (
                                    <tr
                                        key={idx}
                                        className="odd:bg-gray-50 even:bg-white border-b text-sm  shadow-sm hover:shadow-md transition"
                                    >
                                        <td className="py-4 px-3">
                                            <CheckboxCell
                                                checked={selectedRows.includes(item?.application_id)}
                                                onChange={() => handleRowSelect(item?.application_id)}
                                            />
                                        </td>

                                        {columns.map((col) => {
                                            return (
                                                <td key={col.key} className="py-4 px-3 text-[#717576]">
                                                    {col.type === "app" && (
                                                        <AppCell img={item?.icon} app={item?.name} subject={item?.subject_name} id={item?.application_id} permission={permission} />
                                                    )}

                                                    {col.type === "subject" && (
                                                        <p className="py-2 w-[112px] h-[39] rounded-sm bg-blue-100 text-blue-700 text-center text-md font-medium">
                                                            {item?.subject_name}
                                                        </p>
                                                    )}
                                                    {col.type === "educator" && (
                                                        <EducatorCell
                                                            educators={item?.educators}
                                                        />
                                                    )}
                                                    {col.label == "GRADE(S)" && (
                                                        <p>
                                                            {item?.grade_name}
                                                        </p>
                                                    )}
                                                    {col.type === "adopt" &&
                                                        <AdoptionCell count={item?.max_registration} />
                                                    }
                                                    {col.label == "LAST UPDATED" && (
                                                        <p>
                                                            {item?.updated_at}
                                                        </p>
                                                    )}
                                                    {col.type === "status" && <StatusCell status={item?.is_active} />}
                                                    {col.type === "actions" && (
                                                        <TableActions
                                                            item={item}
                                                            userRole={userRole}
                                                            getAllApplicationList={getAllApplicationList}
                                                            getAllApplicationForFilter={getAllApplicationForFilter}
                                                            permission={permission}
                                                        />
                                                    )}
                                                    {col.type === "text" && item[col.key]}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )) :
                                    <tr>
                                        <td colSpan={9}>
                                            <div className="text-center h-[460px] content-center">
                                                <p className="text-gray-400 text-sl">There are currently no applications.</p>
                                            </div>
                                        </td>
                                    </tr>

                            }
                        </tbody>
                    </table>
                </div>
                <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
                    <p>{pagination?.page || '0'} - {pagination?.total || '0'} of {pagination?.pages || '0'} Pages</p>
                    <div className="flex gap-2">
                        <button className={`border rounded px-3 py-1 ${page === 1 ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            onClick={() => setPage(prev => (prev > 1 ? prev - 1 : prev))}
                            disabled={page === 1} // disable on first page
                        >&lt;</button>
                        <button className={`border rounded px-3 py-1 ${page === pagination?.pages ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            onClick={() => setPage(prev => (prev < pagination?.pages ? prev + 1 : prev))}
                            disabled={page === pagination?.pages}
                        >&gt;</button>
                    </div>
                </div>
            </div>



            {/* Delete Modal */}
            <DeleteModal
                isOpen={isDeleteOpen}
                onClose={() => { setIsDeleteOpen(false); setSelectedRows(''); setSelectAll(false) }}
                onConfirm={handleDelete}
                title='Delete'
                deleteLoader={deleteLoader}
            />
        </div>
    );
};

export default ApplicationTable;
